﻿<template>
  <items-listing-layout class="project-listing">
    <template #header-left>
      <!-- HEADER TITLE -->
      <listing-header
        :is-loading="isLoadingInitialData"
        :is-custom-filter-submitting="isCustomFilterSubmitting"
        :show-sample-ribbon="$permissions.isSampleInternalProjectID(id)"
        :title="name"
        :entity-id="id"
        :entity-type="EEntities.PROJECTS"
        :default-filters="defaultFilterNavItems"
        :custom-filters="customFilterNavItems"
        :current-filter="currentFilterNavItem"
        :total-item-count="totalItemCount"
        :active-assignee-counts="activeAssigneeCounts"
        @save-new-filter="(name) => createCustomFilter(name)"
        @update-custom-filter-name="(name) => updateCustomFilter(name)"
        @delete-custom-filter="(id) => deleteCustomFilter(id)"
        @change-filter="(id) => updateItemTraitSetFromFilterID(id)"
        @select-assignee="(id) => selectAssigneeFilter(id)"
      />
    </template>
    <template #header-right>
      <!-- HEADER OPTIONS -->
      <project-home-header-options
        v-if="$screen.isTabletOrDesktop($mq)"
        :is-loading="isLoadingInitialData"
        :members="members"
        :account-id="accountID"
        :project-id="id"
        :total-item-count="totalItemCount"
        @members-added="getMembers"
        @new-task-clicked="showAddTaskDrawer = true"
        @new-scheduled-task-clicked="showAddScheduledTaskDrawer = true"
        @import-clicked="showCsvImportModal = true"
      />
    </template>
    <template #main-header>
      <!-- MAIN HEADER -->
      <item-filters-panel
        v-model:search-term="itemTraitSet.searchTerm"
        v-model:selected-assignee-ids="itemTraitSet.assigneeIDs"
        v-model:assigned-not-to-you="itemTraitSet.assignedNotToYou"
        v-model:selected-job-appointee-groupings-string="itemTraitSet.jobAppointeeGroupingsString"
        v-model:selected-creator-ids="itemTraitSet.creatorIDs"
        v-model:selected-status-ids="itemTraitSet.statusIDs"
        v-model:status-type="itemTraitSet.statusType"
        v-model:selected-priority-ids="itemTraitSet.priorityIDs"
        v-model:selected-due-type="itemTraitSet.dueType"
        v-model:selected-including-tag-ids="itemTraitSet.includingTagIDs"
        v-model:selected-excluding-tag-ids="itemTraitSet.notIncludingTagIDs"
        v-model:created-within-days="itemTraitSet.createdWithinDays"
        v-model:last-updated-within-days="itemTraitSet.lastUpdatedWithinDays"
        v-model:only-watching="itemTraitSet.onlyWatching"
        v-model:start-due-date="itemTraitSet.startDueDate"
        v-model:end-due-date="itemTraitSet.endDueDate"
        v-model:selected-sort="itemTraitSet.sort"
        v-model:custom-field-filters-string="itemTraitSet.customFieldFiltersString"
        :single-project-id="id"
        :is-disabled="hasSelectedRows"
        :is-loading="isLoadingInitialData"
        :is-default-item-trait-set="isDefaultItemTraitSet"
        :assignee-options="members"
        :job-options="jobs"
        :priority-options="priorities"
        :active-status-options="activeStatuses"
        :inactive-status-options="inactiveStatuses"       
        :tag-options="tags"
        :due-date-options="dueDateOptions"
        :custom-field-options="customFieldsForFiltering"
        @reset-filters="resetItemTraitSet"
        @switch-to-kanban="switchToKanban"
        @settings="handleSettings"
      />
    </template>
    <template #main-content>
      <!-- ITEMS -->
      <home-table
        ref="homeTable"
        :type="EEntities.PROJECTS"
        :table-data="results"
        :custom-fields="customFields"
        :is-loading="isLoadingResults"
        :class="{'project-listing__table-no-page-buffer': !hasPages}"
        class="project-listing__table"
        from-entity-home
        @select-rows="updateSelectedRows"
      />
      <!-- PAGINATION -->
      <el-pagination
        v-if="hasPages"
        :page-size="itemsPerPage"
        :pager-count="5"
        :current-page="currentPage"
        :total="totalItemCount"
        background
        layout="prev, pager, next"
        class="project-listing__pagination"
        @current-change="(page) => changePage(page)"
      />
    </template>
    <template #modals>
      <!-- BULK EDIT (DESKTOP) -->      
      <bulk-edit-panel
        v-if="hasSelectedRows" 
        :selected-ids="selectedItemIDs"
        :selected-entity-ids="[id]"
        :entity-type="EEntities.PROJECTS"
        @edit-canceled="($refs.homeTable as { clearBulkEdit(): void }).clearBulkEdit()"
        @edit-completed="reloadData()"
        @move-items-clicked="showMoveItemsModal=true"
        @add-comment-clicked="showAddCommentModal=true"
      />
      <!-- MOVE PROJECT -->
      <bulk-move-items-modal
        v-if="showMoveItemsModal"
        :is-convo-related="false"
        :entity-id="id"
        :selected-item-ids="selectedItemIDs"
        @edit-canceled="showMoveItemsModal=false"
        @edit-completed="reloadData();showMoveItemsModal=false;"
      />
      <!-- BULK ADD COMMENT -->
      <bulk-add-comment-modal
        v-if="showAddCommentModal"
        :is-convo-related="false"
        :selected-item-ids="selectedItemIDs"
        @edit-canceled="showAddCommentModal=false"
        @edit-completed="reloadData();showAddCommentModal=false;"
      />
      <!-- MOBILE NEW BUTTON -->
      <div v-if="$screen.isMobile($mq) && hasResults" @click="sendToNewTask">
        <dd-icon-button
          :class="{'go-native-ios' : $screen.isGoNativeiOSMobileApp($mq)}"
          :color="ColorAssistant.ProjectColor" 
          icon="plus"
          class="project-listing__create-button" 
          type="circle"
          title="Add task" 
        />
      </div>
      <!-- ADD TASK -->
      <add-task-drawer
        v-model:show-drawer="showAddTaskDrawer"
        :project-id="id"
        :last-access-epoch-time="lastAccessEpochTime"
        for-listing
        @task-created-for-listing="(newEpochTime) => handleTaskAdded(newEpochTime)"
      />
      <!-- CSV Import Modal -->
      <csv-import-modal
        v-model:show-modal="showCsvImportModal"
        :project-id="id"
        :project-name="name"
        @close="showCsvImportModal = false"
        @import-completed="reloadData(); showCsvImportModal = false"
      />
      <!-- ADD SCHEDULED TASK -->
      <add-scheduled-task-drawer
        v-model:show-drawer="showAddScheduledTaskDrawer"
        :project-id="id"
        :selected-date="scheduledTaskDefaultDate"
        @scheduled-task-created="sendToProjectSchedule()"
        @scheduled-task-deleted="showAddScheduledTaskDrawer = false"
      />
      <!-- PROJECT INFO -->
      <template v-if="$screen.isDesktop($mq)">
        <entity-info-flyout
          v-if="!isLoadingInitialData"
          v-model:show="showEntityInfoFlyout"
          :entity-id="id"
          :entity-type="EEntities.PROJECTS"
          :members="members"
          :title="name"
          :workflow="workflow"
        />
      </template>
    </template>
  </items-listing-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import dayjs from '@/helpers/dayjs'

/* Mixins */
import EntityLandingMixin from '@/mixins/entity-landing.mixin'

/* Components */
import ProjectHomeHeaderOptions from './components/project-home-header-options.vue'
import AddScheduledTaskDrawer from '@/screens/project-schedule/components/add-scheduled-task-drawer.vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'
import TextAssistant from '@/helpers/text-assistant'
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities, SortOptionValues } from '@/types'
import {
  IBreadcrumbItemMenuItem,
  IHomeTableItem,
} from '@/types/data-objects/items'
import { IItemTraitSet } from '@/types/data-objects/listing'
import {
  ICustomField,
  IInternalProjectsOutputFilterOptionsForListing,
  ITasksOutputSearchTask,
} from '@/api/types/response.types'
import {
  IInternalProjectsInputManageCustomFilter,
  IInternalProjectsInputUpdateCustomFilter,
} from '@/api/types/request.types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import TasksAPI from '@/api/modules/tasks.api'
import CustomFiltersAPI from '@/api/modules/custom-filters.api'

export default defineComponent({
  name: 'ProjectListing',

  components: {
    ItemsListingLayout,
    ProjectHomeHeaderOptions,
    AddScheduledTaskDrawer,
  },

  extends: EntityLandingMixin,

  data() {
    return {
      ColorAssistant,
      showAddTaskDrawer: false,
      showAddScheduledTaskDrawer: false,
      showCsvImportModal: false,
      customFields: [] as ICustomField[],
    }
  },

  computed: {
    scheduledTaskDefaultDate(): Date {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow
    },
  },

  beforeUnmount() {
    this.endPolling()
  },

  async created() {
    LocalStore.clearProjectDefaultedToKanban(this.id)

    this.isLoadingInitialData = true
    await Promise.all([
      this.fetchDetails(),
      this.fetchFilterOptionsForListing(),
    ])
    this.isLoadingInitialData = false
    LocalStore.lastItemListRouteVisited = RouteAssistant.getProjectListingRoute(
      this.accountID,
      this.id,
      this.$permissions,
      this.name,
    )

    this.startPolling()
    this.postCreated()
    this.fetchDefaultFilterOptionCounts()
    await this.showInfoOnEntityLandingIfNecessary()
  },

  methods: {
    switchToKanban() {
      RouteAssistant.goToProjectKanban(
        this.$router,
        this.$route.params.accountId,
        this.id,
        this.$permissions,
      )
    },
    handleSettings() {
      RouteAssistant.goToProjectSettings(
        this.$router,
        this.$route.params.accountId,
        this.id,
      )
    },
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      // A load that finishes after the user left would otherwise start a poller nothing clears
      if (this.isUnmounted) {
        return
      }
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },

    async reloadData() {
      await Promise.all([
        this.fetchFilterOptionsForListing(),
        this.fetchResults(false),
      ])
      this.fetchDefaultFilterOptionCounts()
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await InternalProjectsAPI.get_LastUpdatedEpochTime(
        this.id,
      )
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await Promise.all([
          this.fetchDetails(),
          this.fetchFilterOptionsForListing(),
          this.fetchResults(false),
        ])
        this.fetchDefaultFilterOptionCounts()
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async fetchDetails() {
      try {
        const details = await InternalProjectsAPI.get_Detail(this.id)
        this.name = details.name
        this.members = details.listPeople
        this.workflow = details.workflow
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async fetchResults(showResultsLoading: boolean = true) {
      this.selectedItemIDs = []
      this.isLoadingResults = showResultsLoading
      const params = {
        page: this.currentPage,
        priority_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.priorityIDs,
        ),
        status_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.statusIDs,
        ),
        status_type: this.itemTraitSet.statusType,
        assignee_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.assigneeIDs,
        ),
        assigned_not_to_you: this.itemTraitSet.assignedNotToYou,
        job_appointee_groupings_string: this.itemTraitSet
          .jobAppointeeGroupingsString,
        including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.includingTagIDs,
        ),
        not_including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.notIncludingTagIDs,
        ),
        start_due_date: this.itemTraitSet.startDueDate,
        end_due_date: this.itemTraitSet.endDueDate,
        due_type: this.itemTraitSet.dueType,
        sort: this.itemTraitSet.sort,
        search_term: this.itemTraitSet.searchTerm,
        only_watching: this.itemTraitSet.onlyWatching,
        created_within_days: this.itemTraitSet.createdWithinDays,
        last_updated_within_days: this.itemTraitSet.lastUpdatedWithinDays,
        custom_field_filters: this.itemTraitSet.customFieldFiltersString,
        creator_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.creatorIDs,
        ),
      }

      try {
        // If hiding the load, don't reset total item count so there
        // is no "pulse" on the dashboard filter picker
        if (showResultsLoading) {
          this.totalItemCount = null
        }
        const response = await TasksAPI.get_Search(
          params.page,
          this.id.toString(),
          params.creator_ids,
          params.sort,
          params.priority_ids,
          params.status_ids,
          params.status_type,
          params.assignee_ids,
          params.assigned_not_to_you,
          params.job_appointee_groupings_string,
          params.including_tag_ids,
          params.not_including_tag_ids,
          params.due_type,
          params.start_due_date,
          params.end_due_date,
          params.search_term,
          params.only_watching,
          params.created_within_days,
          params.last_updated_within_days,
          params.custom_field_filters,
        )

        // Store access epoch time
        this.lastAccessEpochTime = response.accessEpochTime

        // Pagination
        this.totalItemCount = response.totalTaskCount

        // Custom fields flagged to show on listings
        this.customFields = response.listCustomFields || []

        // Results
        // @ts-ignore
        this.results = response.listTasks.map(item =>
          this.mapResultsToTableFormat(item),
        )

        const breadCrumbItems = [] as IBreadcrumbItemMenuItem[]

        response.listTasks.forEach(t => {
          breadCrumbItems.push({
            itemID: t.id,
            entityID: this.id,
            entityName: this.name,
            entityType: EEntities.PROJECTS,
            title: t.title,
            refNumber: t.refNumber,
          } as IBreadcrumbItemMenuItem)
        })

        BreadcrumbAssistant.cacheBreadcrumbItemMenuItems(
          breadCrumbItems,
        )
      } catch (error) {
        LocalStore.clearHomeSavedQuery(this.id)
        this.$error.emit(error)
      } finally {
        this.isLoadingResults = false
      }
    },

    async fetchFilterOptionsForListing() {
      try {
        const response = await InternalProjectsAPI.get_FilterOptionsForListing(
          this.id,
        )
        this.hydrateFilterOptionsForListing(response)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    // Should not await this method as it is non-essential to the function of this screen.
    async fetchDefaultFilterOptionCounts() {
      try {
        // Swallow if timeout (or other error) occurs...
        const counts = await InternalProjectsAPI.get_DefaultFilterOptionCounts(
          this.id,
        )
        this.updateDefaultFilterOptionCounts(
          counts.listDefaultFilters,
          counts.listActiveAssigneeCounts,
        )
      } catch (error) {
        // Swallow
      }
    },

    hydrateFilterOptionsForListing(
      data: IInternalProjectsOutputFilterOptionsForListing,
    ) {
      this.hydrateFilterOptionsForListing_Base(data)
      this.customFilterNavItems = data.listCustomFilters.map(c => ({
        value: ItemFilterAssistant.getCustomFilterIdentifier(c.id),
        label: c.name,
        icon: 'filter',
      }))
      this.customFilterItemTraitSets = data.listCustomFilters.map(c => ({
        id: c.id,
        itemTraitSet: {
          priorityIDs: c.listPriorityIDs ? c.listPriorityIDs : [],
          statusIDs: c.listStatusIDs ? c.listStatusIDs : [],
          statusType: c.statusType ? c.statusType : '',
          assigneeIDs: c.listAssigneeIDs ? c.listAssigneeIDs : [],
          assignedNotToYou: c.assignedNotToYou,
          jobAppointeeGroupingsString: ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
            c.listJobAppointeeGroupings,
          ),
          dueType: c.dueType ? c.dueType : null,
          includingTagIDs: c.listIncludingTagIDs ? c.listIncludingTagIDs : [],
          notIncludingTagIDs: c.listNotIncludingTagIDs
            ? c.listNotIncludingTagIDs
            : [],
          startDueDate: c.startDueDate
            ? dayjs(c.startDueDate).format('YYYY-MM-DD')
            : null,
          endDueDate: c.endDueDate
            ? dayjs(c.endDueDate).format('YYYY-MM-DD')
            : null,
          searchTerm: c.searchTerm ? c.searchTerm : '',
          onlyWatching: c.onlyWatching,
          createdWithinDays: c.createdWithinDays,
          lastUpdatedWithinDays: c.lastUpdatedWithinDays,
          creatorIDs: c.listCreatorIDs ? c.listCreatorIDs : [],
          customerIDs: [], // Never used, so default it so the comparison is equal in isItemTraitSetEquivalent()
          isSpam: false, // Never used, so default it so the comparison is equal in isItemTraitSetEquivalent()
          sort: SortOptionValues.UPDATED_RECENT,
          projectIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
          mailboxIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
          customFieldFiltersString: c.customFieldFiltersString
            ? c.customFieldFiltersString
            : '',
        } as IItemTraitSet,
      }))
    },

    async getMembers() {
      try {
        this.members = await InternalProjectsAPI.get_Members(this.id)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async createCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCreatorIDs: this.itemTraitSet.creatorIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          onlyWatching: this.itemTraitSet.onlyWatching,
          customFieldFiltersString: this.itemTraitSet.customFieldFiltersString,
        } as IInternalProjectsInputManageCustomFilter
        const response = await InternalProjectsAPI.post_CreateCustomFilter(
          this.id,
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async updateCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCreatorIDs: this.itemTraitSet.creatorIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          onlyWatching: this.itemTraitSet.onlyWatching,
          customFieldFiltersString: this.itemTraitSet.customFieldFiltersString,
        } as IInternalProjectsInputUpdateCustomFilter
        const response = await InternalProjectsAPI.put_UpdateCustomFilter(
          this.id,
          ItemFilterAssistant.getIDOfFilterIdentifier(
            this.currentFilterNavItem.value,
          ),
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async deleteCustomFilter(identifier: string) {
      try {
        const response = await CustomFiltersAPI.delete_Delete(
          ItemFilterAssistant.getIDOfFilterIdentifier(identifier),
        )

        this.fetchFilterOptionsForListing()
        if (this.currentFilterNavItem.value !== identifier) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async handleTaskAdded(newEpochTime: number) {
      this.lastAccessEpochTime = newEpochTime
      await this.reloadData()
    },

    /* Listing specific */
    sendToNewTask() {
      RouteAssistant.goToCreateTask(
        this.$router,
        this.accountID,
        this.id,
        this.$permissions,
        this.name,
      )
    },

    sendToProjectSchedule() {
      this.showAddScheduledTaskDrawer = false
      this.$router.push(
        RouteAssistant.getProjectScheduleRoute(this.accountID, this.id),
      )
    },

    mapResultsToTableFormat(item: ITasksOutputSearchTask): IHomeTableItem {
      return {
        ...item,
        parent: item.project,
        entityType: EEntities.PROJECTS,
      }
    },
  },
})
</script>

<style lang="scss">
.project-listing {
  &__create-button {
    position: fixed;
    right: 15px;
    bottom: 20px;

    @include media-breakpoint-down(sm) {
      bottom: 70px;
      &.go-native-ios {
        bottom: 100px;
      }
    }
  }

  &__table-no-page-buffer {
    /* Accounts for mobile footer menu that happens at small */
    @include media-breakpoint-down(sm) {
      margin-bottom: 90px;
    }
  }

  &__pagination {
    margin-top: 24px;
    text-align: center;

    /* Accounts for mobile footer menu that happens at small */
    @include media-breakpoint-down(sm) {
      margin-bottom: 90px;
    }
  }

  .container.main-container {
    /* For home, "mobile" switch happens at medium */
    @include media-breakpoint-down(md) {
      max-width: 100%;
    }
  }
}
</style>
